import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * Composant qui met à jour l'attribut lang de la balise HTML en fonction de la langue actuelle
 * Ce composant ne rend rien visuellement, il modifie seulement le DOM
 */
const HtmlLangUpdater = () => {
  const { language } = useLanguage();
  const { lang } = useParams();
  
  useEffect(() => {
    // Utiliser le paramètre d'URL si disponible, sinon utiliser la langue du contexte
    const currentLang = lang || language;
    
    // Mettre à jour l'attribut lang de la balise HTML
    document.documentElement.setAttribute('lang', currentLang);
    
    // Ajouter les balises hreflang pour le SEO
    updateHreflangTags(currentLang, window.location.pathname);
    
    return () => {
      // Nettoyer les balises hreflang lors du démontage du composant
      removeHreflangTags();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, lang]);
  
  /**
   * Met à jour les balises hreflang dans le head pour le SEO
   */
  const updateHreflangTags = (currentLang, currentPath) => {
    // Supprimer les balises existantes
    removeHreflangTags();
    
    // Obtenir l'URL de base sans la langue
    const pathParts = currentPath.split('/');
    if (pathParts.length >= 2 && (pathParts[1] === 'fr' || pathParts[1] === 'en')) {
      pathParts[1] = ''; // Vider la partie langue pour reconstruire les URLs
    }
    const basePath = pathParts.join('/');
    
    // Créer l'URL canonique (URL actuelle)
    const origin = window.location.origin;
    
    // Créer et ajouter les balises hreflang pour chaque langue
    const languages = ['fr', 'en'];
    languages.forEach(lang => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      
      // Construire l'URL complète pour cette langue
      const hrefPath = `${origin}/${lang}${basePath.replace('//', '/')}`.replace(/\/$/, '');
      link.setAttribute('href', hrefPath);
      
      // Ajouter un attribut data-auto pour pouvoir identifier ces balises plus tard
      link.setAttribute('data-auto-hreflang', 'true');
      
      document.head.appendChild(link);
    });
    
    // Ajouter une balise hreflang x-default pointant vers la version française
    const defaultLink = document.createElement('link');
    defaultLink.setAttribute('rel', 'alternate');
    defaultLink.setAttribute('hreflang', 'x-default');
    defaultLink.setAttribute('href', `${origin}/fr${basePath.replace('//', '/')}`.replace(/\/$/, ''));
    defaultLink.setAttribute('data-auto-hreflang', 'true');
    document.head.appendChild(defaultLink);
  };
  
  /**
   * Supprime les balises hreflang ajoutées automatiquement
   */
  const removeHreflangTags = () => {
    const hreflangTags = document.querySelectorAll('link[data-auto-hreflang="true"]');
    hreflangTags.forEach(tag => tag.remove());
  };
  
  // Ce composant ne rend rien visuellement
  return null;
};

export default HtmlLangUpdater;
